import { MediaBlockMediaTypes, type BlockMedia } from "@do/shared-types";
import { ImageZoom } from "@do/ui/components/image-zoom";
import { cn } from "@do/ui/lib/utils";
import { icons } from "lucide-react";

type MessageMediaProps = {
  id: string;
  media: BlockMedia;
};

/** YouTube video IDs are 11 chars from [A-Za-z0-9_-]. */
const YOUTUBE_ID_RE = /^[a-zA-Z0-9_-]{11}$/;

/**
 * Returns the video id for common YouTube URL shapes (watch, youtu.be, embed, shorts, live, mobile, music).
 * Accepts a bare 11-character id. Returns "" if nothing recognizable is found.
 */
function extractYoutubeId(input: string): string {
  const trimmed = input.trim();
  if (!trimmed) return "";

  if (YOUTUBE_ID_RE.test(trimmed)) return trimmed;

  try {
    const href = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const url = new URL(href);
    const host = url.hostname.replace(/^www\./i, "").toLowerCase();

    if (host === "youtu.be") {
      const segment = url.pathname.split("/").filter(Boolean)[0];
      const id = segment?.split("?")[0] ?? "";
      return YOUTUBE_ID_RE.test(id) ? id : "";
    }

    const isYoutubeHost =
      host === "youtube.com" ||
      host === "m.youtube.com" ||
      host === "music.youtube.com" ||
      host === "www.youtube-nocookie.com" ||
      host === "youtube-nocookie.com" ||
      host.endsWith(".youtube.com");

    if (isYoutubeHost) {
      const v = url.searchParams.get("v");
      if (v && YOUTUBE_ID_RE.test(v)) return v;

      const pathMatch = url.pathname.match(
        /^\/(?:embed|live|shorts|v)\/([a-zA-Z0-9_-]{11})(?:\/|[?#]|$)/
      );
      if (pathMatch) return pathMatch[1];
    }
  } catch {
    // fall through to regex fallback
  }

  const fallback = trimmed.match(
    /(?:[?&]v=|youtu\.be\/|embed\/|shorts\/|live\/)([a-zA-Z0-9_-]{11})(?:[?&#/]|$)/
  );
  return fallback?.[1] ?? "";
}

const className =
  "w-full max-w-(--message-media-max-width) rounded-(--message-media-border-radius)";

type MediaIconProps = MessageMediaProps & { className: string };

// An icon media, shown in a card only: a custom icon `url` keeps its own colours, a Lucide
// `iconName` takes the colour of the text.
export const MediaIcon = ({ id, media, className }: MediaIconProps) => {
  if (media.url) {
    return (
      <img
        id={`${id}-icon-image`}
        className={cn("object-contain shrink-0", className)}
        src={media.url}
        alt={media.caption ?? ""}
      />
    );
  }
  const Icon = media.iconName ? icons[media.iconName as keyof typeof icons] : undefined;
  return Icon ? (
    <Icon id={`${id}-icon`} aria-hidden="true" className={cn("shrink-0", className)} />
  ) : null;
};

export const MessageMedia = ({ id, media }: MessageMediaProps) => {
  switch (media.mediaType) {
    case MediaBlockMediaTypes.Image:
      return (
        <ImageZoom backdropClassName="[&_[data-rmiz-modal-img]]:rounded-(--message-media-border-radius)">
          <img
            id={`${id}-image`}
            className={className}
            src={media.url ?? undefined}
            alt={media.caption}
          />
        </ImageZoom>
      );
    case MediaBlockMediaTypes.Video:
      return (
        <video
          id={`${id}-video`}
          className={className}
          src={media.url ?? undefined}
          controls
          {...(media.caption ? { "aria-label": media.caption } : {})}
        />
      );
    case MediaBlockMediaTypes.Youtube:
      return (
        <iframe
          id={`${id}-youtube`}
          className={cn(className, "aspect-video")}
          src={`https://www.youtube.com/embed/${extractYoutubeId(media.url ?? "")}`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      );
    default:
      return null;
  }
};
